/* eslint-disable @typescript-eslint/no-unused-vars */
import {
	CardContainer,
	DataContainer,
	FinalMessage,
	ComeBackMessage,
	CardImage,
	ItemContainer
} from "./styles";
import lock from "assets/Lock.png";
import lockSmall from "assets/Lock_small.svg";
import Button from "components/Buttons/ThemeButton";
import BonusFlag from "components/BonusFlag";
import { CardIconContainer, CardSubTitle, CardTitle } from "../styles";
import moment from "moment";
import { useState } from "react";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import CountdownV2 from "components/CountdownV2";
import { FreeGiftCardSkeleton } from "./skeleton";
import { onDIPtrigger, selectWebStoreState } from "redux/slices";
import { useAppDispatch, useAppSelector } from "hooks";
import { useNavigate } from "react-router-dom";
import { useWebStoreContext } from "contexts";
import {
	clubFreeGiftAPI,
	useGetFreeGiftQuery,
	useUnlockFreeGiftMutation
} from "redux/apis/clubFreeGift";
import { IClubGiftResponse } from "interfaces";
import { useLinks } from "hooks/useLinks";

enum Status {
	UnlockClickRequired = "UnlockClickRequired",
	UnlockClicked = "UnlockClicked",
	Unlocked = "Unlocked",
	Expired = "Expired",
	Error = "Error"
}

export const FreeGiftCard = () => {
	const dispatch = useAppDispatch();
	const { store } = useAppSelector(selectWebStoreState);
	const { syncUserTracker, nowTime, handleErrorResponse } =
		useWebStoreContext();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const navigate = useNavigate();
	const [unlockFreeGift, { isLoading: isUnlocking }] =
		useUnlockFreeGiftMutation();
	const [isProcessingResponse, setProcessingResponse] =
		useState<boolean>(false);
	const links = useLinks();

	const {
		data,
		isError,
		isLoading,
		refetch
	}: {
		data?: IClubGiftResponse;
		isError: boolean;
		isLoading: boolean;
		refetch: () => void;
	} = useGetFreeGiftQuery("", {
		refetchOnFocus: !isProcessingResponse,
		refetchOnReconnect: !isProcessingResponse,
		pollingInterval: !isProcessingResponse ? 5000 : 0
	});

	const clubFreeGift = data?.clubFreeGift;

	const additionalInfo = [
		{
			status: Status.UnlockClickRequired,
			image: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_unlock_click_required"
			)?.imageUrl,
			imageWide: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_unlock_click_required_wide"
			)?.imageUrl,
			message: "Time left:"
		},
		{
			status: Status.UnlockClicked,
			image: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_unlock_clicked"
			)?.imageUrl,
			imageWide: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_unlock_clicked_wide"
			)?.imageUrl,
			message: "Time left:"
		},
		{
			status: Status.Unlocked,
			image: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_unlocked"
			)?.imageUrl,
			imageWide: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_unlocked_wide"
			)?.imageUrl,
			message: "Next Gift in:"
		},
		{
			status: Status.Expired,
			image: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_expired"
			)?.imageUrl,
			imageWide: store?.globalImages?.find(
				(image) => image.id === "club_free_gift_expired_wide"
			)?.imageUrl,
			message: "Next Gift in:"
		}
	];

	// useEffect(() => {
	// 	console.log("clubFreeGift data updated:", data);
	// }, [data?.clubFreeGift]);

	const handleUnlock = async () => {
		setProcessingResponse(true);
		await unlockFreeGift({})
			.unwrap()
			.then((data: IClubGiftResponse) => {
				if (data?.clubFreeGift?.status === Status.Error) {
					refetch();
				} else {
					dispatch(
						clubFreeGiftAPI.util.updateQueryData("getFreeGift", "", () => data)
					);
				}

				if (typeof data?.clubFreeGift?.numClicksToUnlock === "number") {
					if (data?.clubFreeGift?.numClicksToUnlock === 0) {
						reportEvent("unlock_free_gift_click", { source: "club_shop_page" });
						reportPlaygamiEvent("tp.web_frontend", {
							event_name: "unlock_free_gift_click",
							event_type: "club_free_gift",
							action: "unlock_free_gift_click",
							source: "club_shop_page"
						});
						dispatch(onDIPtrigger({ triggerType: "clubFreeGiftCollect" }));
					} else if (data?.clubFreeGift?.numClicksToUnlock > 0) {
						reportEvent("free_gift_click", { source: "club_shop_page" });
						reportPlaygamiEvent("tp.web_frontend", {
							event_name: "free_gift_click",
							event_type: "club_free_gift",
							action: "free_gift_click",
							source: "club_shop_page"
						});
						dispatch(onDIPtrigger({ triggerType: "clubFreeGiftHelpClick" }));
					}
				}
			})
			.catch((error) => {
				handleErrorResponse(error?.data?.error?.toString());
			})
			.finally(() => {
				setProcessingResponse(false);
			});
	};

	const getUnlockTime = () => {
		return moment
			.duration(clubFreeGift?.unlockDurationMinutes, "minutes")
			.humanize();
	};

	const handleEnd = () => {
		setTimeout(() => {
			refetch();
		}, 1000);
	};

	const handleNotInClub = async () => {
		const userTrackerResponse = await syncUserTracker();
		if (userTrackerResponse) {
			const latestUserTracker = userTrackerResponse.userTracker;
			if (!latestUserTracker.clubId) {
				navigate(links.home.path);
			}
		}
	};

	const view = {
		...clubFreeGift,
		...additionalInfo.find((info) => info.status === clubFreeGift?.status)
	};

	// useEffect(() => {
	// 	console.log("clubFreeGift state:", {
	// 		clubFreeGift,
	// 		isUnlocking,
	// 		isProcessingResponse
	// 	});
	// }, [isUnlocking, clubFreeGift, isProcessingResponse]);

	if (clubFreeGift?.status === Status.Error || isError) {
		if (clubFreeGift?.code === "NotClubMember") {
			handleNotInClub();
			return <FreeGiftCardSkeleton />;
		}
		return <></>;
	}

	if (isLoading) {
		return <FreeGiftCardSkeleton />;
	}

	return (
		<CardContainer color="#EBF4FF" image={view?.imageWide}>
			{view?.unlockClicksNeeded !== view?.numClicksToUnlock && (
				<CountdownV2
					text={view?.message}
					startTime={nowTime}
					endTime={
						view?.status === Status.UnlockClickRequired ||
						view?.status === Status.UnlockClicked
							? view?.endDate ?? ""
							: view?.nextAvailableStartDate ?? ""
					}
					onEnd={handleEnd}
					status={view?.status}
				/>
			)}
			<CardImage src={view?.image}></CardImage>
			<BonusFlag text="FREE" position="right" />
			<DataContainer>
				{view?.status === Status.UnlockClickRequired ||
				view?.status === Status.UnlockClicked ? (
					<>
						<CardTitle>Club Free Gift</CardTitle>
						{view?.unlockClicksNeeded === view?.numClicksToUnlock && (
							<CardSubTitle>Unlock time: {getUnlockTime()}</CardSubTitle>
						)}
						<ItemContainer>
							<CardIconContainer>
								<img
									src={lock}
									style={{
										maxHeight: "60px"
									}}
								/>
							</CardIconContainer>
							<CardSubTitle>
								Earn when {view?.unlockClicksNeeded} members tap UNLOCK
							</CardSubTitle>
						</ItemContainer>
						<FinalMessage>
							x{view?.numClicksToUnlock} needed to unlock
							<img
								src={lockSmall}
								style={{
									marginLeft: "5px"
								}}
							/>
						</FinalMessage>
					</>
				) : view?.status === Status.Unlocked ? (
					<>
						<CardTitle>
							Your Club successfully unlocked the Club Free Gift!
						</CardTitle>
						<ComeBackMessage>
							Be sure to collect your gift in your TriPeaks Inbox!
						</ComeBackMessage>
					</>
				) : view?.status === Status.Expired ? (
					<>
						<CardTitle>Not enough members tapped UNLOCK in time</CardTitle>
						<ComeBackMessage>Come back and try again soon!</ComeBackMessage>
					</>
				) : (
					<>
						<label>Status: {view?.status}</label>
						<label>Code: {view?.code}</label>
					</>
				)}
				{view?.status !== Status.Unlocked && (
					<Button
						variant="contained"
						color="success"
						size="large"
						sx={{
							width: "100%",
							paddingTop: "0.5rem",
							paddingBottom: "0.5rem",
							fontSize: "18.5px",
							Weight: "700",
							height: "57px"
						}}
						disabled={
							view?.status !== Status.UnlockClickRequired ||
							isUnlocking ||
							isProcessingResponse
						}
						onClick={handleUnlock}
					>
						Help Unlock Free Gift
					</Button>
				)}
			</DataContainer>
		</CardContainer>
	);
};
